import React from "react";
import { useState } from "react";
import { CheckCircle, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { submitWebsiteQuery } from "../redux/Thunk/providerThunk";

export default function Form() {
  const dispatch = useDispatch();
  const { isProviderLoading } = useSelector((state) => state.providers);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    country: "",
    phoneCode: "+91",
    phone: "",
    query: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Live validation per field
    let errorMessage = "";

    if (name === "username") {
      if (!value.trim()) errorMessage = "Full name is required";
    }

    if (name === "email") {
      if (!value.trim()) {
        errorMessage = "Email is required";
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        errorMessage = "Invalid email address";
      }
    }

    if (name === "country") {
      if (!value.trim()) errorMessage = "Country is required";
    }

    if (name === "phone") {
      if (!value.trim()) {
        errorMessage = "Phone number is required";
      } else if (!/^[0-9]{10}$/.test(value)) {
        errorMessage = "Phone number must be exactly 10 digits";
      }
    }

    if (name === "query") {
      if (!value.trim()) errorMessage = "Please enter your query";
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMessage,
    }));
  };

  const handleValidation = () => {
    let newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    if (!formData.query.trim()) {
      newErrors.query = "Please enter your query";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = handleValidation();
    if (!isValid) return;

    const payload = {
      full_name: formData.username,
      email: formData.email,
      country: formData.country,
      country_code: formData.phoneCode,
      phone_number: formData.phone,
      query_message: formData.query,
    };

    dispatch(submitWebsiteQuery(payload))
      .unwrap()
      .then((res) => {
        if (res.success) {
          setIsModalOpen(true);
          setFormData({
            username: "",
            email: "",
            country: "",
            phoneCode: "+91",
            phone: "",
            query: "",
          });
          setErrors({});
        } else {
          toast.error(res?.message || "Failed to submit query.");
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Something went wrong.");
      });
  };

  return (
    <>
      {/* FORM SECTION */}
      <div className="w-full  py-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-transparent backdrop-blur-md  rounded-2xl p-6 sm:p-8 md:p-10 ">
            <div className="space-y-6">
              {/* Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm  font-bold text-gray-600 font-body">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full placeholder-[font-body] text-sm mt-2 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none-body outline-none focus:ring-1 focus:ring-orange-400 transition "
                    placeholder="Enter your name"
                  />
                  {errors.username && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.username}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-bold text-gray-900 font-body">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full placeholder-[font-body] text-sm  mt-2 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none-body outline-none focus:ring-1 focus:ring-orange-400 transition "
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Country */}
                <div>
                  <label className="text-sm font-bold text-gray-900 font-body">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full mt-2 placeholder-[font-body] text-sm  px-4 py-3 rounded-xl border border-gray-200 focus:outline-none-body outline-none focus:ring-1 focus:ring-orange-400 transition "
                    placeholder="Enter your country"
                  />
                  {errors.country && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.country}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="text-sm font-bold text-gray-900 font-body">
                    Phone Number
                  </label>

                  <div className="flex mt-2 gap-2">
                    <select
                      name="phoneCode"
                      value={formData.phoneCode}
                      onChange={handleChange}
                      className="w-24 sm:w-24  text-sm  lg:w-32 px-2 py-3 rounded-xl border border-gray-200 focus:outline-none-body outline-none focus:ring-1 focus:ring-orange-400 bg-white "
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+61">+61</option>
                    </select>

                    <input
                      type="tel"
                      name="phone"
                      maxLength={10}
                      value={formData.phone}
                      onChange={handleChange}
                      className="flex-1 px-4 py-3 placeholder-[font-body] text-sm  w-[200px] md:w-full rounded-xl border border-gray-200 focus:outline-none text-body focus:ring-1 focus:ring-orange-400 transition "
                      placeholder="Enter phone number"
                    />
                  </div>

                  {errors.phone && (
                    <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* Query */}
              <div>
                <label className="text-sm font-bold text-gray-900 font-body">
                  Your Query
                </label>
                <textarea
                  name="query"
                  rows="4"
                  value={formData.query}
                  onChange={handleChange}
                  className="w-full placeholder-[font-body] text-sm  mt-2 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none-body outline-none focus:ring-1 focus:ring-orange-400 transition  resize-none"
                  placeholder="Write your message here..."
                />
                {errors.query && (
                  <p className="text-sm text-red-500 mt-1">{errors.query}</p>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isProviderLoading}
                  onClick={handleSubmit}
                  className="w-full sm:w-auto sm:min-w-[200px] bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-full transition duration-300 shadow-md hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProviderLoading ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl relative animate-fadeIn">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-black"
            >
              <X size={20} />
            </button>

            <CheckCircle size={60} className="text-orange-500 mx-auto mb-4" />

            <h3 className="text-2xl font-bold text-gray-800 mb-2 font-body">
              Thank You!
            </h3>

            <p className="text-gray-600">
              Your query has been submitted. We’ll get back to you shortly.
            </p>

            <button
              onClick={() => setIsModalOpen(false)}
              className="mt-6 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-xl transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
